import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Container, Button, Box } from "@mui/material";

// Component imports
import Cosection from '../components/Cosections';
import Blogs from "./Blog";
import Ebooks from "./Ebooks"; // 📚 E-books section (currently commented below in use)
import FreeTools from "./Freetools"; // Optional, also commented below
import Scrolltop from '../components/Scrolltop';
import WhatsAppChatButton from '../components/Whatsapp';
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import "./Resource.css";

const Resources = ({ showQuoteModal, setShowQuoteModal }) => {
  const [activeTab, setActiveTab] = useState("blogs"); // Default tab
  const [heights, setHeights] = useState({}); // Dynamic height tracking
  const tabRefs = useRef({});
  const contentRefs = useRef({});
  const location = useLocation();
  const navigate = useNavigate();
  const modalIntervalRef = useRef(null);
  const scrollPositionRef = useRef(0); // For scroll restore after modal

  // Define tab sections
  const tabs = [
    { id: "blogs", name: "Blogs", component: <Blogs /> },
    // { id: "ebooks", name: "E-books", component: <Ebooks /> }, // 📚 Uncomment to show E-books
    // { id: "free-tools", name: "Tools", component: <FreeTools /> } // 🛠️ Optional Tools tab
  ];

  // Measure height of each tab’s content and set dynamic height
  useEffect(() => {
    const updateHeights = () => {
      const newHeights = {};
      tabs.forEach(tab => {
        const contentRef = contentRefs.current[tab.id];
        if (contentRef) {
          newHeights[tab.id] = contentRef.scrollHeight;
        }
      });
      setHeights(newHeights);
    };

    updateHeights();

    // Show modal if not already open
    const showModalIfNotOpen = () => {
      if (!showQuoteModal) {
        scrollPositionRef.current = window.pageYOffset;
        setShowQuoteModal(true);
      }
    };

    // Trigger modal every 2 minutes if not open
    if (!showQuoteModal) {
      modalIntervalRef.current = setInterval(showModalIfNotOpen, 120000);
    }

    return () => {
      if (modalIntervalRef.current) {
        clearInterval(modalIntervalRef.current);
        modalIntervalRef.current = null;
      }
    };
  }, [showQuoteModal, setShowQuoteModal]);

  // Restore scroll position after modal closes
  useEffect(() => {
    if (!showQuoteModal && scrollPositionRef.current > 0) {
      window.scrollTo(0, scrollPositionRef.current);
      scrollPositionRef.current = 0;
    }
  }, [showQuoteModal]);

  // Restart interval after modal closes
  useEffect(() => {
    if (!showQuoteModal && !modalIntervalRef.current) {
      modalIntervalRef.current = setInterval(() => {
        if (!showQuoteModal) {
          setShowQuoteModal(true);
        }
      }, 120000);
    }

    return () => {
      if (modalIntervalRef.current) {
        clearInterval(modalIntervalRef.current);
        modalIntervalRef.current = null;
      }
    };
  }, [showQuoteModal, setShowQuoteModal]);

  // Handle hash-based tab switching (e.g. #blogs)
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash && tabs.some(tab => tab.id === hash)) {
      setActiveTab(hash);
      setTimeout(() => {
        const element = document.getElementById(`tab-${hash}`);
        if (element) {
          const yOffset = -100;
          const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 300);
    }
  }, [location]);

  // Tab click handler
  const toggleTab = (tabId) => {
    setActiveTab(tabId);
    navigate(`#${tabId}`, { replace: true });
  };

  return (
    <>
      <Navbar />
      <Container className="resources-section">
        {/* Toggle Button UI */}
        <Box className="toggle-buttons">
          {tabs.map((tab) => (
            <Button
              key={tab.id}
              id={`tab-${tab.id}`}
              ref={el => tabRefs.current[tab.id] = el}
              variant={activeTab === tab.id ? "contained" : "outlined"}
              className={`toggle-btn ${activeTab === tab.id ? 'is-active' : ''}`}
              onClick={() => toggleTab(tab.id)}
            >
              {tab.name}
            </Button>
          ))}
        </Box>

        {/* Render Active Tab Content */}
        <div className="resources-content">
          {tabs.map((tab) => (
            <div
              key={tab.id}
              className={`resource-tab-content ${activeTab === tab.id ? 'is-active' : ''}`}
              style={{
                height: activeTab === tab.id ? `${heights[tab.id] || 'auto'}` : '0px',
                overflow: activeTab === tab.id ? 'visible' : 'hidden',
                transition: 'height 0.3s ease-in-out'
              }}
            >
              <div
                className="resource-tab-inner"
                ref={el => contentRefs.current[tab.id] = el}
              >
                {tab.component}
              </div>
            </div>
          ))}
        </div>
      </Container>

      {/* Utility Components */}
      <Scrolltop />
      <WhatsAppChatButton />
      <Cosection setShowQuoteModal={setShowQuoteModal} />
      <Footer />
    </>
  );
};

export default Resources;
